import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { csvImportService } from '../../../services/csvImportService';
import { accountService } from '../../../services/accountService';
import { categoryService } from '../../../services/categoryService';
import { transactionService } from '../../../services/transactionService';

const ImportTransactionsModal = ({ onClose, onImportComplete }) => {
  const [step, setStep] = useState(1); // 1: Upload, 2: Map Columns, 3: Preview & Validate, 4: Review & Edit
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({
    date: null,
    description: null,
    amount: null,
    category: null
  });
  const [selectedAccount, setSelectedAccount] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uncategorizedId, setUncategorizedId] = useState(null);
  const [existingTransactions, setExistingTransactions] = useState([]);
  const [validationSummary, setValidationSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  useEffect(() => {
    loadAccounts();
    loadCategories();
  }, []);

  const loadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const data = await accountService?.getAll();
      console.log('Loaded accounts:', data);
      setAccounts(data || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
      setError('Failed to load accounts. Please try again.');
      setAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await categoryService?.getAll();
      setCategories(data);
      
      // Find or note Uncategorized category
      const uncategorized = data?.find(cat => cat?.name === 'Uncategorized');
      if (uncategorized) {
        setUncategorizedId(uncategorized?.id);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadExistingTransactions = async (accountId) => {
    try {
      const data = await transactionService?.getByAccount(accountId);
      setExistingTransactions(data || []);
    } catch (error) {
      console.error('Error loading existing transactions:', error);
      setExistingTransactions([]);
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError('');
    setLoading(true);

    try {
      const parsed = await csvImportService?.processFile(selectedFile);
      
      if (!parsed || !parsed?.data || parsed?.data?.length === 0) {
        throw new Error('No data found in file. Please check your file format.');
      }
      
      setParsedData(parsed);
      
      // Check if this is a PDF with pre-extracted transactions
      if (parsed?.isPDF) {
        // PDF transactions are already extracted, skip column mapping
        // Just need to select account and proceed to preview
        setStep(2); // Still go to step 2, but it will show account selection only
      } else {
        // CSV file - need column mapping
        // Auto-map columns if headers exist
        if (parsed?.hasHeaders) {
          const autoMapping = csvImportService?.autoMapColumns(parsed?.headers);
          setColumnMapping(autoMapping);
        } else {
          // Reset mapping for manual selection
          setColumnMapping({
            date: null,
            description: null,
            amount: null,
            category: null
          });
        }
        setStep(2);
      }
    } catch (err) {
      console.error('Error parsing file:', err);
      setError(err?.message || 'Failed to parse file. Please check the file format and try again.');
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleColumnMappingChange = (field, value) => {
    // Parse value to integer if it's not empty string
    const parsedValue = value === '' ? null : parseInt(value);
    setColumnMapping(prev => ({ ...prev, [field]: parsedValue }));
  };

  const handlePreviewTransactions = async () => {
    if (!selectedAccount) {
      setError('Please select an account');
      return;
    }

    // For PDFs, transactions are already extracted
    if (parsedData?.isPDF) {
      setError('');
      setValidating(true);
      
      try {
        // Load existing transactions for duplicate detection
        await loadExistingTransactions(selectedAccount);
        
        // Map PDF transactions to include account and category
        const mappedTransactions = parsedData?.data?.map((transaction, index) => ({
          ...transaction,
          id: `temp-${index}`,
          accountId: selectedAccount,
          categoryId: uncategorizedId || '',
          edited: false,
          included: true
        }));
        
        if (!mappedTransactions || mappedTransactions?.length === 0) {
          throw new Error('No transactions found in PDF.');
        }
        
        // Validate each transaction and check for duplicates
        const validatedTransactions = await Promise.all(
          mappedTransactions?.map(async (transaction) => {
            const validation = csvImportService?.validateTransaction(transaction);
            const duplicateCheck = await csvImportService?.checkDuplicates(
              transaction,
              selectedAccount,
              existingTransactions
            );
            
            return {
              ...transaction,
              validation,
              duplicateCheck
            };
          })
        );
        
        setTransactions(validatedTransactions);
        
        // Generate validation summary
        const summary = csvImportService?.getValidationSummary(validatedTransactions);
        setValidationSummary(summary);
        
        setStep(3);
      } catch (err) {
        console.error('Error validating transactions:', err);
        setError(err?.message || 'Failed to validate transactions. Please check your data.');
      } finally {
        setValidating(false);
      }
      return;
    }

    // CSV validation logic (existing code)
    if (columnMapping?.date === null || columnMapping?.description === null || columnMapping?.amount === null) {
      setError('Please map all required columns (Date, Description, Amount)');
      return;
    }

    setError('');
    setValidating(true);
    
    try {
      // Load existing transactions for duplicate detection
      await loadExistingTransactions(selectedAccount);
      
      // Map all rows to transactions
      const mappedTransactions = parsedData?.data?.map((row, index) => {
        const transaction = csvImportService?.mapRowToTransaction(
          row,
          columnMapping,
          parsedData?.hasHeaders,
          parsedData?.headers
        );
        
        return {
          ...transaction,
          id: `temp-${index}`,
          accountId: selectedAccount,
          categoryId: uncategorizedId || '',
          edited: false,
          included: true // Default to included
        };
      });
      
      if (!mappedTransactions || mappedTransactions?.length === 0) {
        throw new Error('No transactions could be mapped from the file.');
      }
      
      // Validate each transaction and check for duplicates
      const validatedTransactions = await Promise.all(
        mappedTransactions?.map(async (transaction) => {
          const validation = csvImportService?.validateTransaction(transaction);
          const duplicateCheck = await csvImportService?.checkDuplicates(
            transaction,
            selectedAccount,
            existingTransactions
          );
          
          return {
            ...transaction,
            validation,
            duplicateCheck
          };
        })
      );
      
      setTransactions(validatedTransactions);
      
      // Generate validation summary
      const summary = csvImportService?.getValidationSummary(validatedTransactions);
      setValidationSummary(summary);
      
      setStep(3);
    } catch (err) {
      console.error('Error validating transactions:', err);
      setError(err?.message || 'Failed to validate transactions. Please check your data.');
    } finally {
      setValidating(false);
    }
  };

  const handleTransactionEdit = (id, field, value) => {
    setTransactions(prev => prev?.map(t => 
      t?.id === id ? { ...t, [field]: value, edited: true } : t
    ));
  };

  const handleToggleInclude = (id) => {
    setTransactions(prev => prev?.map(t => 
      t?.id === id ? { ...t, included: !t?.included } : t
    ));
  };

  const handleImport = async () => {
    setImporting(true);
    setError('');

    try {
      // Filter only included transactions
      const transactionsToImport = transactions?.filter(t => t?.included);
      
      if (transactionsToImport?.length === 0) {
        throw new Error('No transactions selected for import');
      }
      
      // Import all transactions
      const importPromises = transactionsToImport?.map(transaction => {
        const { id, edited, included, validation, duplicateCheck, ...transactionData } = transaction;
        return transactionService?.create(transactionData);
      });

      await Promise.all(importPromises);
      
      onImportComplete?.();
      onClose();
    } catch (err) {
      console.error('Error importing transactions:', err);
      setError(err?.message || 'Failed to import transactions');
    } finally {
      setImporting(false);
    }
  };

  const renderUploadStep = () => (
    <div className="space-y-24">
      <div className="text-center">
        <div className="mx-auto w-64 h-64 bg-primary/10 rounded-full flex items-center justify-center mb-16">
          <Icon name="Upload" className="w-32 h-32 text-primary" />
        </div>
        <h3 className="text-xl font-heading font-semibold text-foreground mb-8">
          Upload CSV or PDF
        </h3>
        <p className="text-sm text-muted-foreground font-caption mb-24">
          Select a file containing your transactions. Supports CSV and PDF files from any device.
        </p>
      </div>

      <div className="border-2 border-dashed border-border rounded-lg p-32 text-center hover:border-primary transition-colors">
        <input
          type="file"
          accept=".csv,.pdf,application/pdf,text/csv"
          onChange={handleFileSelect}
          className="hidden"
          id="file-upload"
          disabled={loading}
        />
        <label htmlFor="file-upload" className="cursor-pointer">
          <Icon name="FileText" className="w-48 h-48 text-muted-foreground mx-auto mb-16" />
          <p className="text-sm font-caption text-foreground mb-8">
            {file ? file?.name : 'Click to select file or drag and drop'}
          </p>
          <p className="text-xs text-muted-foreground font-caption">
            CSV or PDF files supported (max 10MB)
          </p>
        </label>
      </div>

      {loading && (
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-8"></div>
          <p className="text-sm text-muted-foreground font-caption">Processing file...</p>
        </div>
      )}

      <div className="bg-muted/30 rounded-lg p-16">
        <h4 className="text-sm font-semibold text-foreground mb-8">Supported Formats:</h4>
        <ul className="text-xs text-muted-foreground font-caption space-y-4">
          <li>• CSV files with or without headers</li>
          <li>• PDF bank statements with transaction tables</li>
          <li>• Files from mobile devices, tablets, and computers</li>
        </ul>
      </div>
    </div>
  );

  const renderMappingStep = () => {
    // For PDF files, show simplified account selection only
    if (parsedData?.isPDF) {
      return (
        <div className="space-y-24">
          <div>
            <h3 className="text-xl font-heading font-semibold text-foreground mb-8">
              PDF Import - Select Account
            </h3>
            <p className="text-sm text-muted-foreground font-caption mb-12">
              We've automatically extracted {parsedData?.transactionCount || parsedData?.data?.length} transactions from your PDF.
            </p>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-12">
              <h4 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-8 flex items-center gap-8">
                <Icon name="CheckCircle" className="w-16 h-16" />
                Automatic Extraction Complete
              </h4>
              <ul className="text-xs text-green-800 dark:text-green-200 font-caption space-y-4 list-disc list-inside">
                <li>Dates, descriptions, and amounts have been automatically detected</li>
                <li>Transaction types (income/expense) have been identified</li>
                <li>Select the account these transactions belong to and continue</li>
              </ul>
            </div>
          </div>

          <div className="space-y-16">
            <Select
              label="Select Account"
              value={selectedAccount}
              onChange={(value) => setSelectedAccount(value)}
              options={[
                { value: '', label: 'Choose an account...', disabled: true },
                ...(loadingAccounts 
                  ? [{ value: 'loading', label: 'Loading accounts...', disabled: true }]
                  : accounts?.map(account => ({
                      value: account?.id,
                      label: `${account?.name} (${account?.type})`
                    }))
                )
              ]}
              loading={loadingAccounts}
              required
            />
          </div>

          {/* Preview first few transactions */}
          {parsedData?.data?.length > 0 && (
            <div className="bg-muted/30 rounded-lg p-16">
              <h4 className="text-sm font-semibold text-foreground mb-12">Preview (First 3 Transactions):</h4>
              <div className="space-y-8">
                {parsedData?.data?.slice(0, 3)?.map((transaction, index) => (
                  <div key={index} className="text-xs font-caption bg-background rounded p-8 border border-border">
                    <div className="grid grid-cols-3 gap-8">
                      <div>
                        <span className="text-muted-foreground">Date:</span>
                        <span className="ml-4 text-foreground">{transaction?.date}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Description:</span>
                        <span className="ml-4 text-foreground">{transaction?.description?.substring(0, 30)}{transaction?.description?.length > 30 ? '...' : ''}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Amount:</span>
                        <span className="ml-4 text-foreground">${transaction?.amount?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-12">
              <p className="text-sm text-red-800 dark:text-red-200 font-caption">{error}</p>
            </div>
          )}

          <div className="flex justify-between pt-16">
            <Button
              variant="outline"
              onClick={() => {
                setStep(1);
                setFile(null);
                setParsedData(null);
                setSelectedAccount('');
                setError('');
              }}
            >
              <Icon name="ArrowLeft" className="w-16 h-16 mr-8" />
              Back
            </Button>
            <Button
              onClick={handlePreviewTransactions}
              disabled={!selectedAccount || validating}
            >
              {validating ? (
                <>
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mr-8"></div>
                  Validating...
                </>
              ) : (
                <>
                  Continue to Preview
                  <Icon name="ArrowRight" className="w-16 h-16 ml-8" />
                </>
              )}
            </Button>
          </div>
        </div>
      );
    }

    // CSV column mapping UI (existing code)
    return (
      <div className="space-y-24">
      <div>
        <h3 className="text-xl font-heading font-semibold text-foreground mb-8">
          Map Columns
        </h3>
        <p className="text-sm text-muted-foreground font-caption mb-12">
          {parsedData?.hasHeaders 
            ? 'We detected headers in your file. Verify the column mapping below.'
            : 'No headers detected. Please map each column to the correct field.'}
        </p>
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-12">
          <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-8 flex items-center gap-8">
            <Icon name="Info" className="w-16 h-16" />
            How to use this step:
          </h4>
          <ol className="text-xs text-blue-800 dark:text-blue-200 font-caption space-y-4 list-decimal list-inside">
            <li>First, select which account these transactions belong to</li>
            <li>Then, map each CSV column to the corresponding transaction field</li>
            <li>Date, Description, and Amount are required fields</li>
            <li>Category is optional - transactions will be marked as "Uncategorized" if not mapped</li>
            <li>Preview the first row below to verify your mapping is correct</li>
          </ol>
        </div>
      </div>

      <div className="space-y-16">
        <Select
          label="Select Account"
          value={selectedAccount}
          onChange={(value) => setSelectedAccount(value)}
          options={[
            { value: '', label: 'Choose an account...', disabled: true },
            ...(loadingAccounts 
              ? [{ value: 'loading', label: 'Loading accounts...', disabled: true }]
              : accounts?.map(account => ({
                  value: account?.id,
                  label: `${account?.name} (${account?.type})`
                }))
            )
          ]}
          loading={loadingAccounts}
          required
        />

        <div className="grid grid-cols-2 gap-16">
          <Select
            label="Date Column"
            value={columnMapping?.date ?? ''}
            onChange={(value) => handleColumnMappingChange('date', value)}
            options={[
              { value: '', label: 'Select an option', disabled: true },
              ...(parsedData?.hasHeaders 
                ? parsedData?.headers?.map((header, index) => ({
                    value: index,
                    label: header
                  }))
                : parsedData?.data?.[0] && Array.isArray(parsedData?.data?.[0])
                  ? parsedData?.data?.[0]?.map((_, index) => ({
                      value: index,
                      label: `Column ${index + 1}`
                    }))
                  : []
              )
            ]}
            required
          />

          <Select
            label="Description Column"
            value={columnMapping?.description ?? ''}
            onChange={(value) => handleColumnMappingChange('description', value)}
            options={[
              { value: '', label: 'Select an option', disabled: true },
              ...(parsedData?.hasHeaders 
                ? parsedData?.headers?.map((header, index) => ({
                    value: index,
                    label: header
                  }))
                : parsedData?.data?.[0] && Array.isArray(parsedData?.data?.[0])
                  ? parsedData?.data?.[0]?.map((_, index) => ({
                      value: index,
                      label: `Column ${index + 1}`
                    }))
                  : []
              )
            ]}
            required
          />

          <Select
            label="Amount Column"
            value={columnMapping?.amount ?? ''}
            onChange={(value) => handleColumnMappingChange('amount', value)}
            options={[
              { value: '', label: 'Select an option', disabled: true },
              ...(parsedData?.hasHeaders 
                ? parsedData?.headers?.map((header, index) => ({
                    value: index,
                    label: header
                  }))
                : parsedData?.data?.[0] && Array.isArray(parsedData?.data?.[0])
                  ? parsedData?.data?.[0]?.map((_, index) => ({
                      value: index,
                      label: `Column ${index + 1}`
                    }))
                  : []
              )
            ]}
            required
          />

          <Select
            label="Category Column (Optional)"
            value={columnMapping?.category ?? ''}
            onChange={(value) => handleColumnMappingChange('category', value)}
            options={[
              { value: '', label: 'Select an option', disabled: true },
              ...(parsedData?.hasHeaders 
                ? parsedData?.headers?.map((header, index) => ({
                    value: index,
                    label: header
                  }))
                : parsedData?.data?.[0] && Array.isArray(parsedData?.data?.[0])
                  ? parsedData?.data?.[0]?.map((_, index) => ({
                      value: index,
                      label: `Column ${index + 1}`
                    }))
                  : []
              )
            ]}
          />
        </div>
      </div>

      {parsedData?.data?.length > 0 && (
        <div className="bg-muted/30 rounded-lg p-16">
          <p className="text-sm font-caption text-foreground mb-8">
            Preview of first row:
          </p>
          <div className="text-xs font-mono text-muted-foreground space-y-4">
            {parsedData?.hasHeaders ? (
              parsedData?.headers?.map((header, index) => {
                const firstRow = parsedData?.data?.[0];
                const value = firstRow?.[header];
                return (
                  <div key={index}>{header}: {value}</div>
                );
              })
            ) : (
              parsedData?.data?.[0] && Array.isArray(parsedData?.data?.[0]) ? (
                parsedData?.data?.[0]?.slice(0, 5)?.map((value, index) => (
                  <div key={index}>Column {index + 1}: {value}</div>
                ))
              ) : null
            )}
          </div>
        </div>
      )}

      <div className="flex gap-12">
        <Button
          variant="outline"
          onClick={() => setStep(1)}
          className="flex-1"
        >
          Back
        </Button>
        <Button
          onClick={handlePreviewTransactions}
          disabled={!selectedAccount || columnMapping?.date === null || columnMapping?.description === null || columnMapping?.amount === null || validating}
          className="flex-1"
        >
          {validating ? 'Validating...' : 'Validate & Preview'}
        </Button>
      </div>
    </div>
    );
  };

  const renderValidationStep = () => {
    const includedCount = transactions?.filter(t => t?.included)?.length;
    const errorCount = transactions?.filter(t => !t?.validation?.isValid && t?.included)?.length;
    const warningCount = transactions?.filter(t => t?.validation?.hasWarnings && t?.included)?.length;
    const duplicateCount = transactions?.filter(t => t?.duplicateCheck?.isDuplicate && t?.included)?.length;

    return (
      <div className="space-y-24">
        <div>
          <h3 className="text-xl font-heading font-semibold text-foreground mb-8">
            Validation Results
          </h3>
          <p className="text-sm text-muted-foreground font-caption">
            Review data quality checks and duplicate detection results before importing.
          </p>
        </div>

        {/* Validation Summary */}
        <div className="grid grid-cols-4 gap-12">
          <div className="bg-primary/10 rounded-lg p-16 text-center">
            <div className="text-2xl font-bold text-primary mb-4">{includedCount}</div>
            <div className="text-xs text-muted-foreground font-caption">To Import</div>
          </div>
          <div className={`rounded-lg p-16 text-center ${errorCount > 0 ? 'bg-error/10' : 'bg-muted/30'}`}>
            <div className={`text-2xl font-bold mb-4 ${errorCount > 0 ? 'text-error' : 'text-muted-foreground'}`}>
              {errorCount}
            </div>
            <div className="text-xs text-muted-foreground font-caption">Errors</div>
          </div>
          <div className={`rounded-lg p-16 text-center ${warningCount > 0 ? 'bg-yellow-500/10' : 'bg-muted/30'}`}>
            <div className={`text-2xl font-bold mb-4 ${warningCount > 0 ? 'text-yellow-600' : 'text-muted-foreground'}`}>
              {warningCount}
            </div>
            <div className="text-xs text-muted-foreground font-caption">Warnings</div>
          </div>
          <div className={`rounded-lg p-16 text-center ${duplicateCount > 0 ? 'bg-orange-500/10' : 'bg-muted/30'}`}>
            <div className={`text-2xl font-bold mb-4 ${duplicateCount > 0 ? 'text-orange-600' : 'text-muted-foreground'}`}>
              {duplicateCount}
            </div>
            <div className="text-xs text-muted-foreground font-caption">Duplicates</div>
          </div>
        </div>

        {/* Transaction List with Validation */}
        <div className="max-h-[400px] overflow-y-auto space-y-12">
          {transactions?.map((transaction) => {
            const hasErrors = !transaction?.validation?.isValid;
            const hasWarnings = transaction?.validation?.hasWarnings;
            const isDuplicate = transaction?.duplicateCheck?.isDuplicate;
            
            return (
              <div 
                key={transaction?.id} 
                className={`rounded-lg p-16 border-2 ${
                  hasErrors ? 'border-error/30 bg-error/5' : isDuplicate ?'border-orange-500/30 bg-orange-500/5': hasWarnings ?'border-yellow-500/30 bg-yellow-500/5': 'border-border bg-muted/30'
                }`}
              >
                <div className="flex items-start gap-12">
                  <Checkbox
                    checked={transaction?.included}
                    onChange={() => handleToggleInclude(transaction?.id)}
                    className="mt-4"
                  />
                  
                  <div className="flex-1 space-y-8">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-foreground">{transaction?.description}</div>
                        <div className="text-sm text-muted-foreground font-caption">
                          {transaction?.date} • ${transaction?.amount?.toFixed(2)} • {transaction?.type}
                        </div>
                      </div>
                      
                      <div className="flex gap-8">
                        {hasErrors && (
                          <div className="flex items-center gap-4 text-error text-xs">
                            <Icon name="AlertCircle" className="w-16 h-16" />
                            Error
                          </div>
                        )}
                        {hasWarnings && !hasErrors && (
                          <div className="flex items-center gap-4 text-yellow-600 text-xs">
                            <Icon name="AlertTriangle" className="w-16 h-16" />
                            Warning
                          </div>
                        )}
                        {isDuplicate && (
                          <div className="flex items-center gap-4 text-orange-600 text-xs">
                            <Icon name="Copy" className="w-16 h-16" />
                            Duplicate
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Validation Issues */}
                    {transaction?.validation?.issues?.length > 0 && (
                      <div className="space-y-4">
                        {transaction?.validation?.issues?.map((issue, idx) => (
                          <div 
                            key={idx} 
                            className={`text-xs font-caption flex items-center gap-8 ${
                              issue?.severity === 'error' ? 'text-error' : 'text-yellow-600'
                            }`}
                          >
                            <Icon name="Info" className="w-12 h-12" />
                            <span>{issue?.field}: {issue?.message}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Duplicate Matches */}
                    {isDuplicate && transaction?.duplicateCheck?.matches?.length > 0 && (
                      <div className="bg-background rounded p-12 space-y-4">
                        <div className="text-xs font-semibold text-orange-600">Possible duplicate of:</div>
                        {transaction?.duplicateCheck?.matches?.slice(0, 2)?.map((match, idx) => (
                          <div key={idx} className="text-xs text-muted-foreground font-caption">
                            {match?.date} - {match?.description} - ${match?.amount?.toFixed(2)} ({match?.matchType} match)
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {errorCount > 0 && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-16">
            <div className="flex items-center gap-8 text-error mb-8">
              <Icon name="AlertCircle" className="w-16 h-16" />
              <span className="text-sm font-semibold">Action Required</span>
            </div>
            <p className="text-xs text-muted-foreground font-caption">
              {errorCount} transaction{errorCount > 1 ? 's have' : ' has'} errors. You can exclude them or proceed to edit in the next step.
            </p>
          </div>
        )}

        <div className="flex gap-12">
          <Button
            variant="outline"
            onClick={() => setStep(2)}
            className="flex-1"
          >
            Back
          </Button>
          <Button
            onClick={() => setStep(4)}
            disabled={includedCount === 0}
            className="flex-1"
          >
            Continue to Review ({includedCount})
          </Button>
        </div>
      </div>
    );
  };

  const renderReviewStep = () => {
    const includedTransactions = transactions?.filter(t => t?.included);
    
    return (
      <div className="space-y-24">
        <div>
          <h3 className="text-xl font-heading font-semibold text-foreground mb-8">
            Review & Edit Transactions
          </h3>
          <p className="text-sm text-muted-foreground font-caption">
            Make final edits before importing {includedTransactions?.length} transactions.
          </p>
        </div>

        <div className="max-h-[400px] overflow-y-auto space-y-12">
          {includedTransactions?.map((transaction) => (
            <div key={transaction?.id} className="bg-muted/30 rounded-lg p-16 space-y-12">
              <div className="grid grid-cols-2 gap-12">
                <Input
                  label="Date"
                  type="date"
                  value={transaction?.date}
                  onChange={(e) => handleTransactionEdit(transaction?.id, 'date', e?.target?.value)}
                />
                <Input
                  label="Amount"
                  type="number"
                  step="0.01"
                  value={transaction?.amount}
                  onChange={(e) => handleTransactionEdit(transaction?.id, 'amount', parseFloat(e?.target?.value))}
                />
              </div>
              <Input
                label="Description"
                value={transaction?.description}
                onChange={(e) => handleTransactionEdit(transaction?.id, 'description', e?.target?.value)}
              />
              <div className="grid grid-cols-2 gap-12">
                <Select
                  label="Type"
                  value={transaction?.type}
                  onChange={(value) => handleTransactionEdit(transaction?.id, 'type', value)}
                  options={[
                    { value: 'income', label: 'Income' },
                    { value: 'expense', label: 'Expense' },
                    { value: 'transfer', label: 'Transfer' }
                  ]}
                />
                <Select
                  label="Category"
                  value={transaction?.categoryId}
                  onChange={(value) => handleTransactionEdit(transaction?.id, 'categoryId', value)}
                  options={[
                    { value: uncategorizedId || '', label: 'Uncategorized' },
                    ...categories
                      ?.filter(cat => cat?.type === transaction?.type)
                      ?.map(category => ({
                        value: category?.id,
                        label: category?.name
                      }))
                  ]}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="bg-primary/10 rounded-lg p-16">
          <p className="text-sm font-caption text-foreground">
            <strong>{includedTransactions?.length}</strong> transactions ready to import
          </p>
        </div>

        <div className="flex gap-12">
          <Button
            variant="outline"
            onClick={() => setStep(3)}
            className="flex-1"
            disabled={importing}
          >
            Back
          </Button>
          <Button
            onClick={handleImport}
            disabled={importing || includedTransactions?.length === 0}
            className="flex-1"
          >
            {importing ? 'Importing...' : `Import ${includedTransactions?.length} Transactions`}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-16">
      <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-background border-b border-border p-24 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-heading font-bold text-foreground">
              Import Transactions
            </h2>
            <p className="text-sm text-muted-foreground font-caption mt-4">
              Step {step} of 4
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            disabled={importing}
          >
            <Icon name="X" className="w-24 h-24" />
          </button>
        </div>

        <div className="p-24">
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-16 mb-24">
              <p className="text-sm text-error font-caption">{error}</p>
            </div>
          )}

          {step === 1 && renderUploadStep()}
          {step === 2 && renderMappingStep()}
          {step === 3 && renderValidationStep()}
          {step === 4 && renderReviewStep()}
        </div>
      </div>
    </div>
  );
};

export default ImportTransactionsModal;